/**
 * Local, dependency-free replacements for viem's `formatUnits` / `parseUnits`.
 *
 * These are byte-for-byte ports of viem's implementations (see
 * `unit-conversion.test.ts`, which asserts parity against viem across a large
 * input matrix). They exist so that display/formatting helpers
 * (`format-crypto-amount.ts`, `token-amounts.ts`) — which load eagerly on the
 * initial render — do not pull the entire viem/ox/@noble surface (~200 kB gzip)
 * into the initial bundle. Wallet/tx code that genuinely needs viem imports it
 * directly and stays behind its existing lazy boundaries.
 */

/**
 * Divides a bigint by 10**decimals and formats it as a decimal string.
 * Port of viem's `formatUnits`. Trailing zeros in the fraction are trimmed.
 *
 * @example
 * formatUnits(420000000000n, 9) // "420"
 * formatUnits(1500000n, 6)      // "1.5"
 */
export function formatUnits(value: bigint, decimals: number): string {
  let display = value.toString();

  const negative = display.startsWith("-");
  if (negative) {
    display = display.slice(1);
  }

  display = display.padStart(decimals, "0");

  const integer = display.slice(0, display.length - decimals);
  let fraction = display.slice(display.length - decimals);

  fraction = fraction.replace(/(0+)$/u, "");

  return `${negative ? "-" : ""}${integer || "0"}${
    fraction ? `.${fraction}` : ""
  }`;
}

/**
 * Multiplies a decimal string by 10**decimals into a bigint.
 * Port of viem's `parseUnits`, including its rounding of over-long fractions.
 * Throws on input that is not a (optionally signed) decimal number.
 *
 * @example
 * parseUnits("420", 9)  // 420000000000n
 * parseUnits("1.5", 6)  // 1500000n
 */
export function parseUnits(value: string, decimals: number): bigint {
  if (!/^(-?)([0-9]*)\.?([0-9]*)$/u.test(value)) {
    throw new Error(`Number \`${value}\` is not a valid decimal number.`);
  }

  let [integer = "", fraction = "0"] = value.split(".");

  const negative = integer.startsWith("-");
  if (negative) {
    integer = integer.slice(1);
  }

  // trim trailing zeros.
  fraction = fraction.replace(/(0+)$/u, "");

  // round off if the fraction is larger than the number of decimals.
  if (decimals === 0) {
    if (Math.round(Number(`.${fraction}`)) === 1) {
      integer = `${BigInt(integer) + 1n}`;
    }
    fraction = "";
  } else if (fraction.length > decimals) {
    const left = fraction.slice(0, decimals - 1);
    const unit = fraction.slice(decimals - 1, decimals);
    const right = fraction.slice(decimals);

    const rounded = Math.round(Number(`${unit}.${right}`));
    fraction =
      rounded > 9
        ? `${BigInt(left) + 1n}0`.padStart(left.length + 1, "0")
        : `${left}${rounded}`;

    if (fraction.length > decimals) {
      fraction = fraction.slice(1);
      integer = `${BigInt(integer) + 1n}`;
    }

    fraction = fraction.slice(0, decimals);
  } else {
    fraction = fraction.padEnd(decimals, "0");
  }

  return BigInt(`${negative ? "-" : ""}${integer}${fraction}`);
}
